// Premium, selectable prize card: glass surface, cursor-following spotlight,
// 3D tilt, an animated gradient border on hover, and a richer icon presentation.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RAFFLE_NS } from './translations';
import { TiltCard } from './interactions';
import { CheckIcon, ArrowRightIcon } from './icons';

const PrizeCard = ({ prize, active = false, index = 0, reducedMotion = false, onSelect }) => {
  const { t } = useTranslation(RAFFLE_NS);
  const { Illustration } = prize;
  // Show the generated photo when available; if the file is missing (not yet
  // dropped into public/raffle/) the onError handler falls back to the SVG art.
  const [photoOk, setPhotoOk] = useState(Boolean(prize.image));
  const title = t(prize.titleKey);

  const animateProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 36 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-60px' },
        transition: { duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] },
      };

  const priceBadge = prize.priceMdl != null && (
    <span className="rf-prize__price-badge">
      <span className="rf-prize__price-num">{prize.priceMdl}</span>
      <span className="rf-prize__price-cur">MDL</span>
    </span>
  );

  const ribbon = prize.featured && (
    <span className="rf-prize__ribbon">{t('prizes.featuredBadge')}</span>
  );

  return (
    <TiltCard
      as="article"
      max={6}
      reducedMotion={reducedMotion}
      animateProps={animateProps}
      className={`rf-prize ${active ? 'rf-prize--active' : ''}`}
    >
      <span className="rf-prize__border" aria-hidden="true" />
      <span className="rf-prize__spotlight" aria-hidden="true" />
      <span className="rf-prize__sheen" aria-hidden="true" />

      {active && (
        <span className="rf-prize__chip">
          <CheckIcon size={14} /> {t('prizes.selected')}
        </span>
      )}

      {prize.image && photoOk ? (
        <div className="rf-prize__photo">
          <span className="rf-prize__photo-ring" />
          <img
            className="rf-prize__photo-img"
            src={prize.image}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setPhotoOk(false)}
          />
          {priceBadge}
          {ribbon}
        </div>
      ) : (
        <div className="rf-prize__art">
          <span className="rf-prize__art-glow" />
          <span className="rf-prize__art-ring" />
          <Illustration className="rf-prize__illustration" />
          {priceBadge}
          {ribbon}
        </div>
      )}

      <h3 className="rf-prize__title">{title}</h3>
      <p className="rf-prize__desc">{t(prize.descKey)}</p>

      <button
        type="button"
        className="rf-prize__select"
        aria-pressed={active}
        onClick={() => onSelect?.(prize.id)}
      >
        {t('prizes.select')}
        <ArrowRightIcon size={18} />
      </button>
    </TiltCard>
  );
};

export default PrizeCard;
